
import { NativeFeatures } from '@/services/nativeFeatures';

// Types
export interface AnimationPosition {
  x: number;
  y: number;
}

// Simplified star animation - only supporting the giant star
export const animateStarCollection = (
  startPosition: AnimationPosition,
  targetElement?: HTMLElement,
  _count: number = 5
): void => {
  // Shake target if provided
  if (targetElement) {
    targetElement.classList.add('animate-shake');
    setTimeout(() => {
      targetElement.classList.remove('animate-shake');
    }, 500);
  }
};

// Firework particle creation
const createFireworkParticle = (container: HTMLDivElement, x: number, y: number, color: string) => {
  const particle = document.createElement('div');
  particle.classList.add('absolute', 'w-3', 'h-3', 'rounded-full', 'firework-particle');
  particle.style.left = `${x}px`;
  particle.style.top = `${y}px`;
  particle.style.backgroundColor = color;
  
  // Random direction and distance
  const angle = Math.random() * Math.PI * 2;
  const distance = 80 + Math.random() * 150; // Increased distance for bigger effect
  const duration = 0.6 + Math.random() * 1.2; // Slightly longer duration
  
  particle.style.transform = 'translate(-50%, -50%)';
  particle.animate(
    [
      { transform: 'translate(-50%, -50%) scale(1)', opacity: 1 },
      { 
        transform: `translate(
          calc(-50% + ${Math.cos(angle) * distance}px), 
          calc(-50% + ${Math.sin(angle) * distance}px)
        ) scale(0)`, 
        opacity: 0 
      }
    ],
    { 
      duration: duration * 1000, 
      easing: 'cubic-bezier(0.1, 0.8, 0.2, 1)'
    }
  );
  
  container.appendChild(particle);
  
  // Remove the particle when animation is done
  setTimeout(() => {
    particle.remove();
  }, duration * 1000);
};

// Create a bunch of fireworks
const createFireworks = (container: HTMLDivElement, x: number, y: number, count: number = 60) => { // Increased count further
  const colors = ['#F97316', '#8B5CF6', '#D946EF', '#0EA5E9', '#ea384c', '#10B981'];
  
  for (let i = 0; i < count; i++) {
    setTimeout(() => {
      createFireworkParticle(
        container, 
        x, 
        y, 
        colors[Math.floor(Math.random() * colors.length)]
      );
    }, Math.random() * 300); // Spread out the fireworks over more time
  }
};

// Trophy shake animation
const animateTrophyShake = (trophy: HTMLDivElement) => {
  const keyframes = [
    { transform: 'rotate(-5deg) scale(1.1)' },
    { transform: 'rotate(5deg) scale(1.1)' },
    { transform: 'rotate(-5deg) scale(1.1)' },
    { transform: 'rotate(5deg) scale(1.1)' },
    { transform: 'rotate(-3deg) scale(1.1)' },
    { transform: 'rotate(3deg) scale(1.1)' },
    { transform: 'rotate(0deg) scale(1.1)' },
    { transform: 'scale(1)' }
  ];
  
  trophy.animate(keyframes, {
    duration: 1000,
    easing: 'ease-in-out'
  });
};

// Celebration animation for milestones
export const celebrateAchievement = (achievementTitle?: string): void => {
  // Trigger impact haptic feedback for achievement unlocked
  NativeFeatures.hapticImpact();
  
  const container = document.createElement('div');
  container.classList.add('fixed', 'inset-0', 'flex', 'items-center', 'justify-center', 'pointer-events-none', 'z-50');
  document.body.appendChild(container);
  
  // Create center trophy
  const trophyContainer = document.createElement('div');
  trophyContainer.classList.add('flex', 'flex-col', 'items-center', 'z-10');
  container.appendChild(trophyContainer);
  
  const trophy = document.createElement('div');
  trophy.classList.add('text-8xl', 'text-yellow-400', 'animate-trophy-celebrate');
  trophy.innerHTML = '🏆';
  trophyContainer.appendChild(trophy);
  
  // Add a glowing background behind the trophy
  const glow = document.createElement('div');
  glow.classList.add('absolute', 'w-40', 'h-40', 'rounded-full', 'bg-yellow-400', 'opacity-25', 'blur-xl');
  trophy.appendChild(glow);
  
  // Add "Trophy Unlocked" label below the trophy (smaller text)
  const label = document.createElement('div');
  label.classList.add('text-sm', 'font-bold', 'text-yellow-400', 'mt-2', 'animate-fade-in');
  label.innerText = 'TROPHY UNLOCKED';
  trophyContainer.appendChild(label);
  
  // Add trophy name if provided
  if (achievementTitle) {
    const nameLabel = document.createElement('div');
    nameLabel.classList.add('text-2xl', 'font-bold', 'text-white', 'mt-1', 'animate-fade-in');
    nameLabel.innerText = achievementTitle;
    trophyContainer.appendChild(nameLabel);
  }
  
  // Get window dimensions for fireworks placement
  const { innerWidth, innerHeight } = window;
  const centerX = innerWidth / 2;
  const centerY = innerHeight / 2;
  
  // Create multiple firework bursts with more particles
  setTimeout(() => {
    createFireworks(container, centerX - 150, centerY - 150, 80);
    createFireworks(container, centerX + 150, centerY - 150, 80);
  }, 200);
  
  setTimeout(() => {
    createFireworks(container, centerX, centerY - 200, 90);
  }, 400);
  
  setTimeout(() => {
    createFireworks(container, centerX - 200, centerY + 50, 80);
    createFireworks(container, centerX + 200, centerY + 50, 80);
  }, 600);
  
  // Add more bursts for BIGGER FIREWORKS
  setTimeout(() => {
    createFireworks(container, centerX - 100, centerY + 100, 70);
    createFireworks(container, centerX + 100, centerY + 100, 70);
  }, 800);
  
  // Animate trophy shake
  animateTrophyShake(trophy);
  
  // Remove after animation completes
  setTimeout(() => {
    container.classList.add('animate-fade-out');
    setTimeout(() => {
      container.remove();
    }, 500);
  }, 3500);
};
